import { useEffect, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { trpc } from "@/trpc/client";
import { NOT_FOUND_HEAD_FALLBACK } from "@/lib/brandIcons";
import { previewRedirectMessage } from "@/lib/blog/previewRedirectMessage";

// Compatibility redirect for the OLD preview URL (#475, supersedes the #423
// in-dashboard themed preview). Preview moved to the blog renderer on the
// workspace's custom domain, authorised by a short-lived signed token minted by
// `createPreviewToken` (the workspaceId now lives inside the token, not the URL).
// This route no longer renders any draft content — the unauthenticated public
// content shell the old route exposed is gone. It exists only so a stale bookmark
// / mid-rollout tab hitting `/preview/<workspaceId>/<postId>` doesn't 404: it
// re-mints a token (auth required) and forwards to the new preview URL. An
// unauthenticated visitor just sees the redirect state, never a post.
export const Route = createFileRoute("/preview/$workspaceId/$postId")({
  ssr: false,
  // This shim never has blog/post data to build a publication favicon from —
  // it just re-mints a token and forwards. The root route omits its icon
  // links on every `/preview*` match regardless (#1099 / US2), so without
  // `NOT_FOUND_HEAD_FALLBACK` the redirect shim would carry no icon at all.
  head: () => ({
    meta: [
      { title: "Preview" },
      { name: "robots", content: "noindex, nofollow" },
    ],
    ...NOT_FOUND_HEAD_FALLBACK,
  }),
  component: PreviewRedirect,
});

function PreviewState({ message }: { message: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0a0a0a] px-6 text-center font-mono text-sm text-white/60">
      {message}
    </div>
  );
}

function PreviewRedirect() {
  const { workspaceId, postId } = Route.useParams();
  const [error, setError] = useState<string | null>(null);
  const mintPreview = useMutation({
    ...trpc.authenticatedRouterFunction.createPreviewToken.mutationOptions(),
    onSuccess: ({ url }) => {
      // Replace (not push) so the Back button skips this redirect shim.
      window.location.replace(url);
    },
    onError: (err) => setError(previewRedirectMessage(err)),
  });

  // Mint exactly once per unique {workspaceId, postId}. Keying off the params
  // (rather than an empty-dep run-once) means that if the router reuses this
  // component instance for a different preview URL — same route, changed params,
  // no unmount — we mint a fresh token instead of getting stuck on "Opening
  // preview…". The ref still guards against React StrictMode (dev), which
  // intentionally double-invokes effects and would otherwise mint two tokens and
  // fire competing redirects for the same params.
  const mintedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const key = `${workspaceId}/${postId}`;
    if (mintedKeyRef.current === key) return;
    mintedKeyRef.current = key;
    mintPreview.mutate({ workspaceId, postId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, postId]);

  return <PreviewState message={error ?? "Opening preview…"} />;
}
